import { DataRefreshEvent, type PendingTalentReferralDto } from "@fp/admin-api-client";
import {
  getGetPendingTalentReferralsQueryKey,
  useDenyTalentReferral,
  useGetPendingTalentReferrals,
  useSendTalentReferralInvite,
} from "@fp/admin-api-client/talent-referrals/talent-referrals.client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";
import { getErrorMessage } from "@/lib/axios";
import { redirectUnlessCapability } from "@/lib/organization-capabilities";
import { toast } from "@/lib/toast";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useUserContext } from "@/components/user-context";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { formatPhoneNumber } from "@fp/ui/components/base/input/input-phone";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

export const Route = createFileRoute("/referrals")({
  beforeLoad: ({ context: { queryClient } }) =>
    redirectUnlessCapability(queryClient, "canListTalentReferrals"),
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { orgId } = useUserContext();
  const [sendingInviteId, setSendingInviteId] = useState<string | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyModalReferral, setDenyModalReferral] = useState<{ id: string; name: string } | null>(null);

  const invalidateReferrals = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetPendingTalentReferralsQueryKey() });
  }, [queryClient]);
  useDataRefresh(DataRefreshEvent.ReferralCreated, invalidateReferrals);
  useDataRefresh(DataRefreshEvent.ReferralInvited, invalidateReferrals);

  const { data: pendingReferralsResponse, isLoading } = useGetPendingTalentReferrals({
    query: { queryKey: [...getGetPendingTalentReferralsQueryKey(), { orgId }], enabled: !!orgId },
  });
  const pendingReferrals = pendingReferralsResponse?.data;

  const sendInviteMutation = useSendTalentReferralInvite({
    mutation: {
      onSuccess: () => {
        invalidateReferrals();
        toast.success("The invitation will be sent shortly");
        setSendingInviteId(null);
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, "Failed to send the invitation. Please try again."));
        setSendingInviteId(null);
      },
    },
  });

  const denyMutation = useDenyTalentReferral({
    mutation: {
      onSuccess: () => {
        invalidateReferrals();
        toast.success("Referral denied");
        setDenyingId(null);
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, "Failed to deny the referral. Please try again."));
        setDenyingId(null);
      },
    },
  });

  const getProspectName = (referral: PendingTalentReferralDto) => {
    if (referral.prospect.lastName) {
      return `${referral.prospect.firstName} ${referral.prospect.lastName}`;
    }
    return referral.prospect.firstName;
  };

  const getReferrerName = (referral: PendingTalentReferralDto) => {
    if (referral.referringTalentFirstName || referral.referringTalentLastName) {
      return `${referral.referringTalentFirstName || ""} ${referral.referringTalentLastName || ""}`.trim();
    }
    return "Unknown";
  };

  const handleSendInvite = (referralId: string) => {
    setSendingInviteId(referralId);
    sendInviteMutation.mutate({ id: referralId });
  };

  const handleDeny = (referral: PendingTalentReferralDto) => {
    setDenyModalReferral({ id: referral.id, name: getProspectName(referral) });
  };

  const handleDenyConfirm = () => {
    if (denyModalReferral) {
      setDenyingId(denyModalReferral.id);
      denyMutation.mutate({ id: denyModalReferral.id });
      setDenyModalReferral(null);
    }
  };

  const getContactInfo = (referral: PendingTalentReferralDto) => {
    const parts: string[] = [];
    if (referral.prospect.email) parts.push(referral.prospect.email);
    if (referral.prospect.phone) parts.push(formatPhoneNumber(referral.prospect.phone));
    return parts.join(" • ");
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Talent Member Referrals"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="space-y-4">
        {isLoading ? (
          <PageLoading />
        ) : pendingReferrals && pendingReferrals.length > 0 ? (
          <div className="space-y-4">
            {pendingReferrals.map((referral) => (
              <div
                key={referral.id}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-900">
                      {getProspectName(referral)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {getContactInfo(referral)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Referred by {getReferrerName(referral)} on {formatLocalDate(referral.createdDate, "dateWithYear")}
                    </div>
                  </div>
                </div>

                {referral.note && (
                  <div className="bg-gray-50 rounded-md p-3">
                    <div className="text-xs font-medium text-gray-500 mb-1">Note</div>
                    <div className="text-sm text-gray-700">{referral.note}</div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex gap-3">
                    <Button
                      color="secondary-destructive"
                      onClick={() => handleDeny(referral)}
                      isLoading={denyingId === referral.id}
                      isDisabled={
                        sendingInviteId === referral.id || denyingId === referral.id
                      }
                      className="flex-1"
                    >
                      Deny
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => handleSendInvite(referral.id)}
                      isLoading={sendingInviteId === referral.id}
                      isDisabled={
                        sendingInviteId === referral.id || denyingId === referral.id
                      }
                      className="flex-1"
                    >
                      Send Invite
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No pending referrals</div>
            <p className="text-sm text-gray-400">
              When talent members refer their friends, they'll appear here for review.
            </p>
          </div>
        )}
      </PageContentContainer>
      <ConfirmationModal
        isOpen={!!denyModalReferral}
        onClose={() => setDenyModalReferral(null)}
        onConfirm={handleDenyConfirm}
        title="Deny Referral"
        message={`Are you sure you want to deny the referral for ${denyModalReferral?.name}?`}
        confirmText="Deny"
        cancelText="Cancel"
        isLoading={!!denyingId}
      />
    </AdminLayout>
  );
}